import { useRegisterSW } from 'virtual:pwa-register/react';

// The controllerchange -> reload guard lives in ./reloadOnControllerChange, imported for
// side effects from main.tsx so it arms on every route (not just while UpdateToast is
// mounted inside AppShell). See that module for why.

/**
 * Registers the service worker (autoUpdate: polls for a new SW in the background,
 * activates it as soon as one is found) and shows a small "new version ready" toast that
 * lets the user reload on their own terms — checkboxes/forms mid-interaction shouldn't
 * get yanked out from under them (DESIGN-BRIEF §5).
 */
export function UpdateToast(): JSX.Element | null {
  const {
    needRefresh: [needRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      // Poll for a fresh service worker every hour so long-lived tabs still get updates.
      if (!registration) return;
      setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });

  if (needRefresh) {
    return (
      <div
        role="status"
        className="fixed inset-x-4 bottom-[calc(72px+env(safe-area-inset-bottom))] z-30 mx-auto flex max-w-sm items-center justify-between gap-3 rounded-xl bg-inverse px-4 py-3 text-sm text-inverse-fg shadow-lg lg:bottom-4 lg:left-4 lg:right-auto"
      >
        <span>A new version is ready.</span>
        <button
          type="button"
          onClick={() => void updateServiceWorker(true)}
          className="shrink-0 rounded-lg bg-inverse-fg px-3 py-1.5 font-semibold text-inverse"
        >
          Refresh
        </button>
      </div>
    );
  }

  if (offlineReady) {
    return (
      <div
        role="status"
        className="fixed inset-x-4 bottom-[calc(72px+env(safe-area-inset-bottom))] z-30 mx-auto flex max-w-sm items-center justify-between gap-3 rounded-xl bg-inverse px-4 py-3 text-sm text-inverse-fg shadow-lg lg:bottom-4 lg:left-4 lg:right-auto"
      >
        <span>AwwYeah is ready to work offline.</span>
        <button
          type="button"
          onClick={() => setOfflineReady(false)}
          className="shrink-0 rounded-lg px-2 py-1 text-inverse-fg/70"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return null;
}
